/**
 * Two heroes, one camera, one frame stream (CAM-07, D-076(1)/(2), recon §B.1–B.3).
 *
 * This file is deliberately thin, and that is the whole design. It owns exactly two things:
 *
 *   1. **One {@link LaneResolver}**, which is the single component allowed to decide whose body a
 *      reading is (recon §B.2's one-owner rule). Nothing here re-derives identity, and neither do
 *      the detectors or the per-lane adapters — they are never in a position to, because what they
 *      are handed is a frame containing one body.
 *   2. **Two ordinary {@link CameraInputAdapter}s** — the shipped Phase 3 adapter, unmodified in
 *      kind, one per hero. Hero 1 is the left lane and Hero 2 the right (MIN §6, GDS §7).
 *
 * Everything a cooperative sitting must do therefore falls out of two single-player sittings rather
 * than being written a second time: the silent baseline, retractions-never-onsets, neutral
 * re-acquisition, discard-in-flight on a pause edge, the confidence gate, the uncertainty seam. If
 * a rule held for one child in Phase 3 it holds per lane here, because it is literally the same
 * code reading the same shape.
 *
 * **What each lane is told, and why:**
 *
 *   - *attributed* — the lane's own body, as a frame with exactly one pose in it. Ordinary play.
 *   - *absent* — an empty frame, which is byte-for-byte what the single-body path does when a room
 *     empties. The shipped `trackingLossPauseMs` ladder times it, so one child's loss is noticed at
 *     exactly the speed a solo child's would be, and — D-076(2)(a) — the **other lane keeps
 *     playing**. A sibling's camera trouble must never hold a child mid-answer.
 *   - *acquiring* — nothing at all. The Slice 0′ spike measured 5–7 frames to acquire a second body
 *     (D-077(2)(d)); saying anything during that window would open every cooperative sitting by
 *     declaring a child missing. Silence here is not a claim.
 *   - *ambiguous* — `reportIdentityUncertain()`: retractions, a neutral re-baseline, and a record
 *     that the camera could not confidently observe this child. Never an event, never a departure.
 *
 * **What it deliberately does not do:** it builds no recovery surface, holds no round, and knows
 * nothing about missions. Whether a family sees an invitation when a lane goes quiet is Slice E′'s
 * decision, made from {@link CooperativeCameraAdapter.laneStatus} — the seam this file exists to
 * leave clean.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PlayerId } from '../../shared/types/ids';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { SemanticEventBus } from '../events/semantic-event-bus';
import { HERO_1_PLAYER_ID, HERO_2_PLAYER_ID } from '../keyboard/keyboard-adapter';
import {
  attributedPose,
  createLaneResolver,
  laneObservation,
  LANE_SIDES,
  type LaneAmbiguityReason,
  type LaneAttribution,
  type LaneResolution,
  type LaneResolver,
  type LaneSide,
} from '../pose/lane-resolver';
import type { PoseCalibrationThresholds } from '../pose/pose-calibration';
import type { PoseFrame } from '../pose';
import {
  createCameraInputAdapter,
  DEFAULT_POSE_INPUT_CONTEXT,
  type CameraInputAdapter,
  type CameraInputReconfiguration,
  type InputUncertaintyCapability,
  type InputUncertaintyRecorder,
  type PoseFrameSource,
  type PoseInputContext,
} from './camera-input-adapter';

/** Which hero stands in which lane. Fixed, and not an option: MIN §6 and GDS §7 both say so. */
export const LANE_PLAYER_IDS: Readonly<Record<LaneSide, PlayerId>> = {
  left: HERO_1_PLAYER_ID,
  right: HERO_2_PLAYER_ID,
};

export interface CooperativeLaneOptions {
  /** This child's measurements, from their own run of hero training (Slice C′). */
  readonly calibration?: PoseCalibrationThresholds | undefined;
}

export interface CooperativeCameraAdapterOptions {
  bus: SemanticEventBus;
  /** Where frames come from. Optional so the pair can be driven frame by frame in tests. */
  tracker?: PoseFrameSource | null;
  /** Where "I could not see that" goes, per child. */
  analytics?: InputUncertaintyRecorder | null;
  /**
   * One lane's camera half, or **`null` for a hero who is playing on the keyboard** (Slice F′,
   * D-076(5)).
   *
   * `undefined` and `null` mean different things here, deliberately. `undefined` is the shipped
   * "this hero has no measurements yet" — a camera lane with default thresholds. `null` is
   * "this seat is not answering with a body at all": no adapter is built for it, no detector reads
   * it, no event of theirs can exist, and no uncertainty is recorded about a child nothing is
   * watching.
   *
   * The **resolver still sees both lanes** either way, and that is the point of doing it here
   * rather than by building a one-lane pair: the child on the keys is still standing in the room,
   * and a resolver that did not know about them would have nothing to separate the camera child's
   * body *from* (D-079(2)'s separation floor).
   */
  left?: CooperativeLaneOptions | null | undefined;
  right?: CooperativeLaneOptions | null | undefined;
  tunables?: GameplayTunables;
  now?: () => number;
  inputContext?: PoseInputContext;
  /** Both lanes join a mission already under way (S5-2, per lane). */
  joiningMissionInProgress?: boolean;
  /**
   * The sitting's uncertainty diagnostics — **one signal for the room, never one per child**
   * (D-076(6)).
   *
   * A per-child input-quality reading is a de facto sibling comparison, and the non-competitive
   * invariant's spirit reaches input quality even though its letter scopes to UI, narration and the
   * parent summary. What stays per child is participation, which Phase 2 already reports neutrally.
   */
  onUncertaintyObserved?:
    ((detail: { capability: InputUncertaintyCapability; confidence: number }) => void) | undefined;
  onUncertaintyCleared?: (() => void) | undefined;
  /**
   * A lane's own body was attributed this frame (S3-6).
   *
   * Handed straight to the lane adapters rather than raised here, so the sitting's "the camera read
   * somebody" fact has exactly one definition whether one child is playing or two. A lane nothing is
   * reading — a hero on the keys, or a released lane — never raises it, which is right: the resolver
   * still decides about their body, but nothing observes it and nothing is recorded about them.
   */
  onBodyAttributed?: (() => void) | undefined;
  /**
   * Which run of camera frames this is (S4-1).
   *
   * A number that changes whenever the stream underneath this pair is torn down and rebuilt. The
   * frame subscription deliberately survives a `stop()`/`start()` — the stage stops and restarts the
   * camera around a mid-mission hero training while the pair stays attached — so from in here a new
   * stream looks exactly like the old one, and a warm, already-permitted restart can deliver its
   * first frame well inside the timestamp bound that would otherwise notice. The child in front of
   * the lens afterwards is not necessarily the child who was there before.
   *
   * The timestamp rule remains the fallback for the stalls nothing knows about; this is the case
   * where something *does* know, and it should not have to be inferred from a clock.
   */
  streamGeneration?: (() => number) | undefined;
  /** Injected in tests; the default is the shipped resolver on the shipped thresholds. */
  resolver?: LaneResolver;
}

export interface CooperativeCameraAdapter {
  attach(): void;
  detach(): void;
  readonly attached: boolean;
  /** Both heroes' ids, left lane first. */
  readonly playerIds: readonly PlayerId[];
  /**
   * The heroes whose bodies are actually being read, left lane first (Slice F′).
   *
   * A subset of {@link playerIds} in a mixed sitting, and it shrinks when a lane is released. Every
   * per-lane surface and every per-lane monitor is built from this rather than from `playerIds`, so
   * nothing can end up watching a seat that nothing is reading.
   */
  readonly cameraPlayerIds: readonly PlayerId[];
  /** One hero's own adapter, or null when that hero is playing on the keyboard (Slice F′). */
  lane(playerId: PlayerId): CameraInputAdapter | null;
  /**
   * This hero stops answering with their body — and **only** this hero (D-076(5)).
   *
   * The one per-child teardown primitive in the app. It detaches that lane's adapter, which is what
   * releases a posture it had latched (D-048(2)), and leaves the resolver two-lane so the sibling's
   * attribution keeps its separation floor. Idempotent, and never reachable for a lane that was
   * never on the camera.
   *
   * Deliberately not "rebuild the pair with one lane": rebuilding re-baselines the *other* hero
   * mid-round (S5-2) and clears their latched zone, which is a child slowed down by their sibling's
   * camera trouble — the one thing D-076(2)(a) forbids.
   */
  releaseLane(playerId: PlayerId): void;
  /** What the resolver last decided about this hero's lane. Nobody else may derive it. */
  laneStatus(playerId: PlayerId): LaneAttribution['kind'];
  /**
   * The lane view a mid-mission hero training reads (S3, D-075/D-076(1)'s one-owner rule).
   *
   * A retraining trainer needs to know which body in the room is the hero it is measuring — which
   * is the identity question, and this sitting already has exactly one thing entitled to answer it.
   * Before this existed the trainer built a **second** resolver on the live stream, uncalibrated,
   * and could reach the opposite verdict from the same frame: gameplay reporting `SWAP_SUSPECTED`
   * while the trainer measured the sibling into the retraining hero's profile.
   *
   * So this hands back the pair's *own* decision rather than a means of making another one. It
   * re-resolves nothing: `resolve(frame)` returns what this pair decided about that very frame, and
   * anything else — a frame the pair has not seen yet, or a different one — is reported as nobody,
   * which the shipped machine reads as "waiting for a body". Failing that way is the point: a
   * trainer that waits costs a grown-up a moment, and a trainer that guesses costs a child their
   * measurements.
   *
   * It carries the sitting's **live** calibrations, deliberately, and that has a recorded cost: a
   * hero whose body this sitting's resolver no longer recognises as theirs (a child who moved a
   * long way towards the camera) cannot be re-measured mid-mission, because nothing in the picture
   * can be confidently called them. Their route is the shipped one — the per-lane refusal takes
   * that hero to their always-live keys (D-083(6), BL-004). Refusing to measure is the PS4-3 rule,
   * and the alternative — dropping the veto so the training can proceed — is how the sibling
   * standing in that half gets measured into a child's profile, which is the defect this exists to
   * close.
   */
  retrainingLaneView(): LaneResolver;
  /** Why a lane is unreadable, when it is — the honest detail behind `laneStatus`. */
  laneAmbiguity(playerId: PlayerId): LaneAmbiguityReason | null;
  readonly inputContext: PoseInputContext;
  setInputContext(context: PoseInputContext): void;
  /** A grown-up's mid-mission override, scoped to the child they chose (D-076(8)). */
  reconfigure(playerId: PlayerId, changes: CameraInputReconfiguration): void;
  /** The mission stopped listening. Both lanes discard what was in flight, and say nothing. */
  neutralize(): void;
  /** The camera stopped producing frames at all: both lanes take the ordinary loss path. */
  reportFramesStopped(): void;
  /** Reads one frame for both lanes. The tracker calls this; tests call it directly. */
  observe(frame: PoseFrame): void;
}

function sideOf(playerId: PlayerId): LaneSide {
  return playerId === HERO_2_PLAYER_ID ? 'right' : 'left';
}

/** Nobody, for a lane view asked about a frame this pair has not read (S3). */
const ABSENT_ATTRIBUTION: LaneAttribution = { kind: 'absent' };

export function createCooperativeCameraAdapter(
  options: CooperativeCameraAdapterOptions,
): CooperativeCameraAdapter {
  const {
    bus,
    tunables = DEFAULT_GAMEPLAY_TUNABLES,
    analytics = null,
    tracker = null,
    now = () => Date.now(),
  } = options;

  const resolver =
    options.resolver ??
    createLaneResolver({
      left: { calibration: options.left?.calibration },
      right: { calibration: options.right?.calibration },
      // One continuity bound for the whole sitting: the resolver reads the same tunable the lane
      // adapters do, so a retune moves both halves of a discontinuity together (S6-7).
      tunables,
    });

  /**
   * One adapter per hero, built exactly as a single-player sitting builds its one.
   *
   * `cameraMode` is not an option: cooperative camera play is **Full-Motion only** for the MVP
   * (D-076(4)) — two children need a lane each, and Compact is the one-child accessibility mode it
   * shipped as. A room that cannot fit two Full-Motion lanes plays cooperatively on the keyboard.
   */
  const lanes: Record<LaneSide, CameraInputAdapter | null> = {
    left: options.left === null ? null : buildLane('left'),
    right: options.right === null ? null : buildLane('right'),
  };

  function buildLane(side: LaneSide): CameraInputAdapter {
    const adapter = createCameraInputAdapter({
      bus,
      playerId: LANE_PLAYER_IDS[side],
      tunables,
      analytics,
      now,
      cameraMode: 'full-motion',
      ...(options[side]?.calibration === undefined
        ? {}
        : { calibration: options[side]?.calibration }),
      ...(options.inputContext === undefined ? {} : { inputContext: options.inputContext }),
      ...(options.joiningMissionInProgress === undefined
        ? {}
        : { joiningMissionInProgress: options.joiningMissionInProgress }),
      onUncertaintyObserved: options.onUncertaintyObserved,
      onUncertaintyCleared: options.onUncertaintyCleared,
      onBodyAttributed: options.onBodyAttributed,
    });
    /**
     * Live from construction, because these adapters have no tracker of their own: frames reach
     * them from `observe()` below, and `attached` is what makes `detach()` release a latched
     * posture rather than return early (D-048(2)). What `attach()` manages here is the **shared**
     * subscription — one frame stream for two lanes, or one `PoseLandmarker` would be read twice.
     */
    adapter.attach();
    return adapter;
  }

  const status: Record<LaneSide, LaneAttribution> = {
    left: { kind: 'absent' },
    right: { kind: 'absent' },
  };

  let attached = false;
  let unsubscribe: (() => void) | null = null;
  let inputContext: PoseInputContext =
    lanes.left?.inputContext ?? lanes.right?.inputContext ?? DEFAULT_POSE_INPUT_CONTEXT;

  /**
   * One lane's view of a frame: its own body, and nothing else in the room.
   *
   * A body the resolver has not **confirmed** is not this lane's view of anything (P-B, S4-3): while
   * the evidence is arguing with the attribution, this lane sees the empty frame it would see if
   * nobody were there, which is the shipped cannot-see path and reaches no onset. The resolver keeps
   * tracking the body meanwhile; that is its business and not this child's detectors'.
   */
  function laneFrame(frame: PoseFrame, attribution: LaneAttribution): PoseFrame {
    const pose = attributedPose(attribution);
    return { ...frame, poses: pose === null ? [] : [pose] };
  }

  /**
   * Whether each lane is inside a run of frames it was actually shown its own body in (S5-1).
   *
   * **Observation continuity is a lane-level fact**, and this is the half of it the frames'
   * timestamps cannot see: the stream is perfectly regular, and this lane is being shown nothing.
   * Every lane adapter decides the timestamp half for itself, on the frames it is handed, which is
   * how a solo sitting gets the same rule with no publisher at all (S5-5) and how a lane that
   * straddles a stream gap gets it whether or not it was being attributed at the time (S5-3).
   */
  const observed: Record<LaneSide, boolean> = { left: true, right: true };

  /**
   * The edge out of continuous observation, and it fires **once** (S5-1).
   *
   * Edge-triggered rather than duration-based, because the interval that has to be caught can be a
   * single 33 ms frame — far below every threshold in the tracking ladder — and because a lane that
   * stays unobserved for a hundred frames has one hole in its observation, not a hundred. Firing on
   * the way *in* is what retracts a held answer the moment its owner is in doubt, rather than
   * leaving it on a child's record for the length of the argument.
   */
  function markUnobserved(side: LaneSide): void {
    if (!observed[side]) return;
    observed[side] = false;
    lanes[side]?.reportObservationGap();
  }

  /**
   * The frame this pair last read, and what it decided about it (S3).
   *
   * Held so a mid-mission retraining can be told the answer instead of working one out. The frame
   * itself is kept, not a timestamp: identity of the object is what proves the trainer is asking
   * about the same picture this pair looked at.
   */
  let lastObserved: { frame: PoseFrame; resolution: LaneResolution } | null = null;
  /** Which run of frames the last observed one belonged to, or null before the first (S4-1). */
  let lastGeneration: number | null = null;

  function observe(frame: PoseFrame): void {
    /**
     * A new stream is a new run of frames, however quickly it arrived (S4-1).
     *
     * The timestamp rule inside the resolver reaches every stall that announces itself to nobody,
     * and a stop/start is usually one of them — but a warm restart of an already-permitted camera
     * can return its first frame in a few hundred milliseconds, inside the bound. Nothing about that
     * speed makes the child in front of the lens the same child, so the boundary itself is the
     * signal and the clock is the fallback, rather than the other way round.
     */
    const generation = options.streamGeneration?.() ?? null;
    if (generation !== null && lastGeneration !== null && generation !== lastGeneration) {
      /**
       * Identity only — deliberately **not** the posture baseline (P-A's boundary, found in review).
       *
       * The two halves of a discontinuity are justified by different facts. *Who* the body is
       * depends on the stream: a new stream may be showing a different child, whatever the clock
       * says, so identity memory cannot survive one. *What the body was doing* depends on whether
       * anybody was watching, and a new stream that goes on delivering frames at cadence has watched
       * the child throughout — D-070's rule, which the mission's own pause already relies on.
       *
       * Baselining postures here as well cost a real answer: the stage stops and starts the camera
       * around a mid-mission hero training, so this boundary lands on the first frame after Resume —
       * exactly when a child is stepping into the portal they mean to answer with — and re-baselining
       * the lane detector there ate the step. Nothing is lost by the narrower rule: a stop/start that
       * really does interrupt the pictures leaves a hole in the frame timestamps, and the gap rule
       * below baselines both halves when it sees one.
       */
      resolver.forgetIdentity();
    }
    lastGeneration = generation;

    // Resolved for **both** lanes even when only one of them is being read, because the child on
    // the keys is still a body in the room: the resolver needs them to keep the camera lane's
    // separation floor honest (D-079(2)) — and this is the one place that could get it wrong.
    const resolution = resolver.resolve(frame);
    lastObserved = { frame, resolution };

    // Left first, always, so two heroes' retractions arrive in a stable order. Nothing depends on
    // it and nothing may: neither lane is ahead of the other (the non-competitive invariant).
    for (const side of LANE_SIDES) {
      const attribution = resolution[side];
      status[side] = attribution;

      /**
       * Was **this lane** observed this frame? (S5-1, and the whole of the lane-level rule.)
       *
       * A frame arriving is not the same sentence as this lane being shown its own body. Two of the
       * four attributions are observation — its body, or the honest empty picture the shipped loss
       * ladder times — and two are not: a lane the resolver is still arguing about (P-B withholds
       * every one of those frames from it) and a lane whose second body has not arrived yet.
       */
      const reading = laneObservation(attribution);
      if (reading === 'observed') observed[side] = true;
      else if (reading === 'unobserved') markUnobserved(side);

      const adapter = lanes[side];
      // A hero playing on the keyboard is not being watched, so nothing about their body is an
      // event, an absence, or an uncertainty. Silence here is not a claim about them.
      if (adapter === null) continue;

      switch (attribution.kind) {
        case 'attributed':
        case 'absent':
          // An unconfirmed attribution reaches here as the empty frame P-B gives it, deliberately:
          // withholding the body must not also stop the shipped `trackingLossPauseMs` ladder from
          // noticing that this lane has had nothing in it for a while.
          adapter.observe(laneFrame(frame, attribution));
          break;
        case 'ambiguous':
          adapter.reportIdentityUncertain();
          break;
        case 'acquiring':
          // Deliberately nothing. A second body that has not arrived yet is not a missing child,
          // and a lane the detectors are not told about cannot start a loss clock about one.
          break;
        default:
          break;
      }
    }
  }

  return {
    get attached() {
      return attached;
    },

    playerIds: [LANE_PLAYER_IDS.left, LANE_PLAYER_IDS.right],

    get cameraPlayerIds(): readonly PlayerId[] {
      return LANE_SIDES.filter((side) => lanes[side] !== null).map((side) => LANE_PLAYER_IDS[side]);
    },

    lane(playerId: PlayerId): CameraInputAdapter | null {
      return lanes[sideOf(playerId)];
    },

    releaseLane(playerId: PlayerId): void {
      const side = sideOf(playerId);
      const adapter = lanes[side];
      if (adapter === null) return;
      // Detaching is what retracts whatever this child was holding — the same release a hero gets
      // when the whole sitting ends, applied to one seat.
      adapter.detach();
      lanes[side] = null;
      status[side] = { kind: 'absent' };
      // Nothing reads this seat any more, so it holds no observation to be continuous with.
      observed[side] = true;
    },

    laneStatus(playerId: PlayerId): LaneAttribution['kind'] {
      return status[sideOf(playerId)].kind;
    },

    retrainingLaneView(): LaneResolver {
      return {
        resolve(frame: PoseFrame): LaneResolution {
          // Never `resolver.resolve()`: reading the stream twice would advance the one resolver's
          // continuity and acquisition state a second time per frame, which is the same defect in a
          // different costume. This reports; it does not decide.
          if (lastObserved === null || lastObserved.frame !== frame) {
            return { left: ABSENT_ATTRIBUTION, right: ABSENT_ATTRIBUTION };
          }
          return lastObserved.resolution;
        },
        setCalibration(): void {
          // Nothing may push measurements at the sitting through a training view: the profile a
          // training produces is applied by the grown-up's Resume, through `reconfigure`.
        },
        forgetIdentity(): void {
          // Nothing to forget, for the same reason as `reset` below: this view holds no state, and
          // a training must not clear the sitting's own memory of where the *sibling* is standing.
        },
        reset(): void {
          // Nothing to forget: this view holds no state of its own, and resetting the sitting's
          // resolver because a training ended would wipe the *sibling's* continuity mid-mission.
        },
      };
    },

    laneAmbiguity(playerId: PlayerId): LaneAmbiguityReason | null {
      const attribution = status[sideOf(playerId)];
      return attribution.kind === 'ambiguous' ? attribution.reason : null;
    },

    get inputContext() {
      return inputContext;
    },

    setInputContext(context: PoseInputContext): void {
      inputContext = context;
      for (const side of LANE_SIDES) lanes[side]?.setInputContext(context);
    },

    reconfigure(playerId: PlayerId, changes: CameraInputReconfiguration): void {
      const side = sideOf(playerId);
      lanes[side]?.reconfigure(changes);
      // The resolver's scale checks read the same measurements, so retraining one child must move
      // them here too — and must not touch the other lane's (checklist row 15's no cross-contamination).
      if (changes.calibration !== undefined) resolver.setCalibration(side, changes.calibration);
    },

    neutralize(): void {
      for (const side of LANE_SIDES) lanes[side]?.neutralize();
    },

    reportFramesStopped(): void {
      /**
       * A stall is a full loss for identity purposes, and the resolver has to be told (S2-3).
       *
       * The lane adapters take the ordinary loss path — that is what retracts a latched posture and
       * announces the departure — but they hold no opinion about who anybody is, and the one thing
       * that does was left believing its last frame was a moment ago. So the memory of whose lane a
       * lone body held survived a camera being unplugged, and the next child in front of the lens
       * inherited it.
       *
       * `forgetIdentity` rather than `reset`: what the frames stopping proves is that nothing can be
       * continuous with anything, not that this sitting never had a body in it. The acquisition
       * timing D-086(2) settled is deliberately untouched, so a recovering sitting is no slower to
       * notice a missing child than a solo one is.
       */
      resolver.forgetIdentity();
      status.left = { kind: 'absent' };
      status.right = { kind: 'absent' };
      // The loss path has just run for both lanes, which is a stronger statement than "not
      // observed": there is no held posture left to invalidate and no episode to be on the wrong
      // side of, so the next frame starts a run rather than looking like the end of one.
      observed.left = true;
      observed.right = true;
      for (const side of LANE_SIDES) lanes[side]?.reportFramesStopped();
    },

    observe,

    attach(): void {
      if (attached) return;
      attached = true;
      unsubscribe = tracker?.subscribe((frame) => observe(frame)) ?? null;
    },

    detach(): void {
      attached = false;
      unsubscribe?.();
      unsubscribe = null;
      resolver.reset();
      status.left = { kind: 'absent' };
      status.right = { kind: 'absent' };
      observed.left = true;
      observed.right = true;
      // Neither lane may leave a posture latched behind it.
      for (const side of LANE_SIDES) lanes[side]?.detach();
    },
  };
}
